<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import TravelKitCard from './TravelKitCard.vue'
  import qicTravelLogo from './assets/qic-travel.svg'
  import ooredooLogo from './assets/ooredoo.svg'
  import { getQicTravelKitUrl } from './TravelKit.helpers'
  import { getOoredooBannerUrl } from '../OoredooBanner/OoredooBanner.helpers'
  import { useEnvironment } from '@/shared/composables/useEnvironment'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { useFeaturesStore } from '@/shared/store'

  interface Props {
    policyId: Nullable<string>
  }

  const props = defineProps<Props>()

  const QIC_TRAVEL_BACKGROUND = 'linear-gradient(180deg, #474BC4 0%, #262BA6 183%), #FFF'
  const OOREDOO_BACKGROUND =
    'linear-gradient(173deg, #DA2024 1.76%, #680D0F 89.95%), linear-gradient(180deg, #474BC4 0%, #262BA6 183%), #FFF'

  const { locale } = useI18n()
  const { environment } = useEnvironment()
  const gaService = useGoogleAnalytics()
  const featuresStore = useFeaturesStore()

  const qicTravelLink = computed(() => getQicTravelKitUrl(environment.value, locale.value))
  const ooredooLink = computed(() => getOoredooBannerUrl(locale.value))

  const sendViewBanner = (title: string) => {
    gaService.sendViewBanner(props.policyId, title)
  }

  onMounted(() => {
    if (featuresStore.isTravelKitOfferEnabled) {
      sendViewBanner('qic_travel_banner')
    }

    if (featuresStore.isOoredooEnabled) {
      sendViewBanner('ooredoo_banner')
    }
  })
</script>

<template>
  <div class="travel-kit">
    <h3 class="heading-3">
      {{ $t('visitors.success_page.travel_kit.header') }}
    </h3>

    <div class="travel-kit__wrapper">
      <TravelKitCard
        v-if="featuresStore.isTravelKitOfferEnabled"
        name="qic-travel"
        :logo="qicTravelLogo"
        :background="QIC_TRAVEL_BACKGROUND"
        :link="qicTravelLink"
      >
        <template #title>{{ $t('visitors.success_page.travel_kit.qic_travel.header') }}</template>
        <template #description>
          {{ $t('visitors.success_page.travel_kit.qic_travel.desc') }}
        </template>
        <template #cta>
          {{ $t('visitors.success_page.travel_kit.qic_travel.explore.button') }}
        </template>
      </TravelKitCard>

      <TravelKitCard
        v-if="featuresStore.isOoredooEnabled"
        name="ooredoo"
        :logo="ooredooLogo"
        :background="OOREDOO_BACKGROUND"
        :link="ooredooLink"
      >
        <template #title>{{ $t('ooredoo_block.header') }}</template>
        <template #description>{{ $t('ooredoo_block.annotation') }}</template>
        <template #cta>{{ $t('ooredoo_block.see_plans_button') }}</template>
      </TravelKitCard>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .travel-kit {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-7);

    &__wrapper {
      display: flex;
      gap: var(--spacing-5);
    }

    @media (max-width: 544px) {
      &__wrapper {
        flex-direction: column;
      }
    }
  }
</style>
